import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Paper,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Chip,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar
} from '@mui/material';
import {
  Edit,
  Save,
  Email,
  Message,
  Webhook,
  NotificationsActive,
  FormatListBulleted as ListIcon,
  Groups as TeamsIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme, alpha } from '@mui/material/styles';
import { NotificationChannelType, AlertSeverity, Exporter, NotificationChannel } from '../types';
import { SubscriberManagement } from '../components/SubscriberManagement';

// ============================================================================
// Types
// ============================================================================

export interface EditNotificationChannelFormData {
  type: NotificationChannelType;
  name: string;
  value: string;
  // Type-specific config (read-only for most types after creation)
  canmailListId?: number;
  email?: string;
  slackWebhookUrl?: string;
  slackChannel?: string;
  teamsWebhookUrl?: string;
  pagerdutyRoutingKey?: string;
  webhookUrl?: string;
  webhookSecret?: string;
  // Subscription settings (editable)
  watchAll: boolean;
  watchExporterIds: number[];
  severities: AlertSeverity[];
  enabled: boolean;
}

interface CanmailList {
  id: number;
  name: string;
  memberCount: number;
}

interface EditNotificationChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (id: number, data: EditNotificationChannelFormData) => Promise<boolean>;
  channel: NotificationChannel | null;
  exporters?: Exporter[];
  canmailLists?: CanmailList[];
}

// ============================================================================
// Constants
// ============================================================================

const CHANNEL_TYPE_CONFIG: Record<
  NotificationChannelType,
  { label: string; icon: React.ElementType; color: string }
> = {
  canmail: { label: 'CANmail List', icon: ListIcon, color: '#1976d2' },
  email: { label: 'Individual Email', icon: Email, color: '#388e3c' },
  slack: { label: 'Slack', icon: Message, color: '#611f69' },
  teams: { label: 'Microsoft Teams', icon: TeamsIcon, color: '#464775' },
  pagerduty: { label: 'PagerDuty', icon: NotificationsActive, color: '#06ac38' },
  webhook: { label: 'Webhook', icon: Webhook, color: '#f57c00' }
};

const SEVERITIES: { value: AlertSeverity; label: string; color: string; icon: React.ElementType }[] = [
  { value: 'critical', label: 'Critical', color: '#d32f2f', icon: ErrorIcon },
  { value: 'warning', label: 'Warning', color: '#ed6c02', icon: WarningIcon },
  { value: 'info', label: 'Info', color: '#0288d1', icon: InfoIcon }
];

// ============================================================================
// Component
// ============================================================================

export const EditNotificationChannelDialog: React.FC<EditNotificationChannelDialogProps> = ({
  open,
  onClose,
  onSave,
  channel,
  exporters = [],
  canmailLists = []
}) => {
  const theme = useTheme();

  // Form state
  const [name, setName] = useState('');
  const [watchAll, setWatchAll] = useState(true);
  const [watchExporterIds, setWatchExporterIds] = useState<number[]>([]);
  const [severities, setSeverities] = useState<AlertSeverity[]>(['critical', 'warning']);
  const [enabled, setEnabled] = useState(true);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Populate form when channel changes
  useEffect(() => {
    if (channel) {
      setName(channel.name || '');
      setWatchAll(channel.watchAll);
      // Convert string exporter IDs to numbers
      setWatchExporterIds(
        channel.watchExporters?.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)) || []
      );
      setSeverities(channel.severities || ['critical', 'warning']);
      setEnabled(true); // Default to enabled if not in type
      setHasChanges(false);
    }
  }, [channel]);

  // Track changes
  useEffect(() => {
    if (channel) {
      const originalExporterIds = channel.watchExporters?.map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)) || [];
      const changed =
        name !== (channel.name || '') ||
        watchAll !== channel.watchAll ||
        JSON.stringify([...watchExporterIds].sort()) !== JSON.stringify([...originalExporterIds].sort()) ||
        JSON.stringify([...severities].sort()) !== JSON.stringify([...(channel.severities || [])].sort());
      setHasChanges(changed);
    }
  }, [name, watchAll, watchExporterIds, severities, enabled, channel]);

  // Reset form when dialog closes
  const handleClose = () => {
    setName('');
    setWatchAll(true);
    setWatchExporterIds([]);
    setSeverities(['critical', 'warning']);
    setEnabled(true);
    setError('');
    setHasChanges(false);
    onClose();
  };

  // Handle severity toggle
  const handleSeverityToggle = (severity: AlertSeverity) => {
    setSeverities((prev) =>
      prev.includes(severity)
        ? prev.filter((s) => s !== severity)
        : [...prev, severity]
    );
  };

  // Validation
  const isValid = () => {
    return (
      name.trim() !== '' &&
      severities.length > 0 &&
      (watchAll || watchExporterIds.length > 0)
    );
  };

  // Handle form submission
  const handleSave = async () => {
    if (!channel || !isValid()) return;

    setSubmitting(true);
    setError('');

    try {
      const formData: EditNotificationChannelFormData = {
        type: channel.type,
        name,
        value: channel.value,
        watchAll,
        watchExporterIds: watchAll ? [] : watchExporterIds,
        severities,
        enabled
      };

      const success = await onSave(channel.id, formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error('Save notification channel failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get channel type config
  const getTypeConfig = () => {
    if (!channel) return null;
    return CHANNEL_TYPE_CONFIG[channel.type];
  };

  if (!channel) return null;

  const typeConfig = getTypeConfig();
  const TypeIcon = typeConfig?.icon || Email;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Edit sx={{ mr: 1 }} />
            Edit Notification Channel
          </Box>
          <Chip
            icon={<TypeIcon sx={{ fontSize: 14 }} />}
            label={typeConfig?.label || channel.type}
            size="small"
            sx={{
              bgcolor: typeConfig ? `${typeConfig.color}15` : undefined,
              color: typeConfig?.color,
              '& .MuiChip-icon': { color: typeConfig?.color }
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Channel Info (Read Only) */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Channel Details (Read Only)
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.grey[50],
              borderRadius: 1,
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: typeConfig?.color || theme.palette.primary.main,
                  mr: 2
                }}
              >
                <TypeIcon sx={{ color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  {channel.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {typeConfig?.label || channel.type}
                  {channel.memberCount ? ` (${channel.memberCount} members)` : ''}
                </Typography>
              </Box>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                The channel type and destination cannot be changed after creation.
                To use a different destination, create a new channel.
              </Typography>
            </Alert>
          </Box>

          {/* Subscriber Management (CANmail only) */}
          {channel.type === 'canmail' && channel.canmailListId && (
            <>
              <Divider sx={{ my: 3 }} />
              <SubscriberManagement listId={channel.canmailListId} />
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Editable Settings */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Editable Settings
          </Typography>

          <TextField
            fullWidth
            label="Channel Name *"
            placeholder="e.g., Engineering Team Alerts"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="A friendly name to identify this channel"
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          {/* Subscription Settings */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Subscription Settings
          </Typography>

          {/* Watch All Toggle */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={watchAll}
                  onChange={(e) => setWatchAll(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">Watch All Exporters</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Receive alerts from all current and future exporters
                  </Typography>
                </Box>
              }
            />
          </Paper>

          {/* Exporter Selection (when not watching all) */}
          {!watchAll && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Select Exporters to Watch
              </Typography>
              <Autocomplete
                multiple
                options={exporters}
                getOptionLabel={(option) => option.displayName || option.name}
                value={exporters.filter((e) => watchExporterIds.includes(e.id))}
                onChange={(_, newValue) => {
                  setWatchExporterIds(newValue.map((e) => e.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search exporters..."
                    helperText={`${watchExporterIds.length} exporter(s) selected`}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.displayName || option.name}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
              />
              {watchExporterIds.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Please select at least one exporter to watch
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Severity Filter */}
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
            Alert Severities
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Only receive alerts matching these severity levels:
          </Typography>

          <FormGroup row>
            {SEVERITIES.map((severity) => {
              const SeverityIcon = severity.icon;
              return (
                <FormControlLabel
                  key={severity.value}
                  control={
                    <Checkbox
                      checked={severities.includes(severity.value)}
                      onChange={() => handleSeverityToggle(severity.value)}
                      sx={{
                        color: severity.color,
                        '&.Mui-checked': { color: severity.color }
                      }}
                    />
                  }
                  label={
                    <Chip
                      icon={<SeverityIcon sx={{ fontSize: 14 }} />}
                      label={severity.label}
                      size="small"
                      sx={{
                        bgcolor: severities.includes(severity.value)
                          ? `${severity.color}20`
                          : 'transparent',
                        border: `1px solid ${severity.color}`,
                        color: severity.color,
                        '& .MuiChip-icon': { color: severity.color }
                      }}
                    />
                  }
                />
              );
            })}
          </FormGroup>

          {severities.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one severity level
            </Alert>
          )}

          {/* Summary */}
          <Paper
            variant="outlined"
            sx={{ p: 2, mt: 3, bgcolor: alpha(theme.palette.grey[500], 0.04) }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Summary
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This channel will receive {severities.join(', ')} alerts from{' '}
              {watchAll
                ? 'all exporters'
                : `${watchExporterIds.length} selected exporter(s)`}
              .
            </Typography>
          </Paper>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || !isValid()}
          loading={submitting}
          startIcon={<Save />}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditNotificationChannelDialog;
